import { Controller } from "@hotwired/stimulus"

// Base class for the in-place photo evidence + AI readout controls
// (reading-capture, pitch-capture). Not registered itself.
//
// On capture the photo uploads immediately as a weighing attachment via
// fetch (so the open modal and unsaved fields survive) and renders as a
// thumbnail beside the fields; when the server has an AI key, a downscaled
// copy is sent for a readout that lands in the still-editable fields for
// the human to verify against that photo.
//
// Subclasses provide: uploadFields(body), thumbAlt, extractionFilename,
// savedMessage, readingMessage, applyExtraction(data), and may override
// beforeCapture(), attachmentUploaded(attachment) and attachmentRemoved(id).
export default class PhotoCaptureController extends Controller {
  static targets = ["input", "evidence", "status"]
  static values = { uploadUrl: String, extractUrl: String, ai: Boolean }

  // Longest side sent to the AI — the evidence keeps the original photo.
  static EXTRACT_MAX_PX = 1568
  // Largest original the AI accepts when the canvas downscale is unavailable.
  static EXTRACT_MAX_BYTES = 4.5 * 1024 * 1024

  connect() {
    this.objectUrls = []
  }

  disconnect() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }

  async capture() {
    const file = this.inputTarget.files[0]
    if (!file) return
    this.inputTarget.value = ""
    if (!this.beforeCapture()) return

    const thumb = this.addThumb(file)
    this.setStatus("Saving photo…", "busy")
    let attachment
    try {
      attachment = await this.upload(file)
    } catch (error) {
      thumb.remove()
      this.setStatus(error.message || "The photo could not be saved — try again.", "error")
      return
    }

    thumb.querySelector("[data-attachment-id]").dataset.attachmentId = attachment.id
    if (attachment.url) thumb.querySelector("a").href = attachment.url
    this.attachmentUploaded(attachment)

    if (!this.aiValue) {
      this.setStatus(this.savedMessage, "ok")
      return
    }

    this.setStatus(this.readingMessage, "busy")
    try {
      const image = await this.extractionImage(file)
      if (!image) {
        this.setStatus("Photo saved — too large to auto-read, enter the value manually.", "warn")
        return
      }
      this.applyExtraction(await this.extract(image))
    } catch (error) {
      this.setStatus(`Photo saved — ${error.message || "the auto-read failed"}. Enter the value manually.`, "warn")
    }
  }

  async removePhoto(event) {
    const button = event.currentTarget
    const id = button.dataset.attachmentId
    if (!id || !window.confirm("Remove this photo?")) return

    const response = await fetch(`${this.uploadUrlValue}/${id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": this.csrfToken(), "Accept": "application/json" }
    })
    if (!response.ok) {
      this.setStatus("The photo could not be removed — try again.", "error")
      return
    }
    button.closest(".reading-thumb")?.remove()
    this.attachmentRemoved(id)
  }

  // ── hooks (defaults) ─────────────────────────────────────────────────────

  beforeCapture() { return true }
  attachmentUploaded() {}
  attachmentRemoved() {}

  // ── upload / extract ─────────────────────────────────────────────────────

  async upload(file) {
    const body = new FormData()
    body.append("weighing_attachment[file]", file)
    this.uploadFields(body)

    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken(), "Accept": "application/json" },
      body
    })
    const data = await response.json().catch(() => ({}))
    if (response.status === 413) throw new Error("The photo is too large for the server — take it at a lower resolution.")
    if (!response.ok) throw new Error(data.error || "The photo could not be saved — try again.")
    return data
  }

  async extract(image) {
    const body = new FormData()
    body.append("image", image)
    const response = await fetch(this.extractUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken(), "Accept": "application/json" },
      body
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || "the auto-read failed")
    return data.data || {}
  }

  // Downscaled JPEG copy for the AI (the API rejects large images); the
  // original already went up as evidence. Falls back to the original when
  // canvas work fails and the file is small enough, else null.
  async extractionImage(file) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" })
      const max = PhotoCaptureController.EXTRACT_MAX_PX
      const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height))
      const canvas = document.createElement("canvas")
      canvas.width = Math.max(1, Math.round(bitmap.width * scale))
      canvas.height = Math.max(1, Math.round(bitmap.height * scale))
      canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      bitmap.close()
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85))
      if (blob) return new File([blob], this.extractionFilename, { type: "image/jpeg" })
    } catch {
      // fall through to the size check below
    }
    return file.size <= PhotoCaptureController.EXTRACT_MAX_BYTES ? file : null
  }

  // Fills a field with a suggestion and marks it AI-filled without
  // triggering the "user edited" handlers.
  fillField(element, value, eventName = "input") {
    this.filling = true
    element.value = value
    element.classList.add("ai-filled")
    element.dispatchEvent(new Event(eventName, { bubbles: true }))
    if (eventName === "input") element.dispatchEvent(new Event("change", { bubbles: true }))
    this.filling = false
  }

  // ── presentation ──────────────────────────────────────────────────────────

  addThumb(file) {
    const url = URL.createObjectURL(file)
    this.objectUrls.push(url)

    const figure = document.createElement("figure")
    figure.className = "reading-thumb"

    const link = document.createElement("a")
    link.className = "reading-thumb__link"
    link.href = url
    link.target = "_blank"
    link.rel = "noopener"
    const img = document.createElement("img")
    img.src = url
    img.alt = this.thumbAlt
    link.appendChild(img)

    const del = document.createElement("button")
    del.type = "button"
    del.className = "reading-thumb__del"
    del.title = "Remove photo"
    del.setAttribute("aria-label", "Remove photo")
    del.dataset.attachmentId = ""
    del.dataset.action = `${this.identifier}#removePhoto`
    const icon = document.createElement("i")
    icon.className = "bi bi-x-lg"
    del.appendChild(icon)

    figure.appendChild(link)
    figure.appendChild(del)
    this.evidenceTarget.appendChild(figure)
    return figure
  }

  setStatus(text, tone) {
    this.statusTarget.textContent = text
    this.statusTarget.dataset.tone = tone
    this.statusTarget.hidden = false
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
